import { createFileRoute } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";
import { useState, useMemo } from "react";
import { LayoutHeader } from "@/Header";
import { SequentialAnimation } from "@/SequentialAnimation";
import { ValidateInput } from "@/Input";
import { Divider } from "@/Divider";
import BusinessAddressPicker from "@/common/BusinessAddressPicker";
import { ValidateSelectList } from "@/SelectList";
import { ValidateControllerSelect } from "@/ControllerSelect";
// import { ValidateCheckbox } from "@/Checkbox";
import { Button } from "@/Button";
import { Dialog } from "@/Dialog";
import {
  useBusinessMypageCompanyInfo,
  useUpdateBusinessMypageCompanyInfo,
} from "~/services/business/myPage";
import { useBusinessLoginSelectList } from "~/services/auth";
import {
  formatBusinessNumberInput,
  formatTelInput,
  transformKeyValueArrayToOptions,
} from "%/dataTransform";

export const Route = createFileRoute("/business/layout/user-business-info")({
  component: RouteComponent,
});

interface BusinessInfoForm {
  email: string;
  companyName: string;
  businessNumber: string;
  companyAddr: string;
  companyAddrDetail: string;
  mainTelNumber: string;
  mainBank: string;
  mainAccountNumber: string;
  depositor: string;
  playgroundYn: string;
  serviceProvides: string;
  facilities: string;
  weightClasses: string;
  maxWeight: number;
  dailyReservCapacity: number | "";
  reservCapacityLimitYn: string;
  agree: boolean;
  longitude: string | number;
  latitude: string | number;
}

function RouteComponent() {
  const { data: companyInfo, isLoading } = useBusinessMypageCompanyInfo();
  const updateMutation = useUpdateBusinessMypageCompanyInfo();
  const { data: selectList } = useBusinessLoginSelectList();

  // 로딩 중이거나 데이터가 없으면 로딩 표시
  if (isLoading || !companyInfo || !selectList) {
    return (
      <>
        <LayoutHeader
          title={"사업자 정보 관리"}
          hasBackBtn
          hasFootPrint={false}
        />
        <div className="flex-auto flex justify-center items-center bg-[#F5F5F5]">
          <div>데이터 로딩 중...</div>
        </div>
      </>
    );
  }

  // 데이터가 완전히 로드된 후에만 실제 컴포넌트 렌더링
  return (
    <BusinessInfoForm
      companyInfo={companyInfo}
      selectList={selectList}
      updateMutation={updateMutation}
    />
  );
}

// 별도 컴포넌트로 분리하여 데이터 로드 후에만 생성
function BusinessInfoForm({
  companyInfo,
  selectList,
  updateMutation,
}: {
  companyInfo: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  selectList: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  updateMutation: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}) {
  // 모달 상태 관리
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState({
    title: "알림",
    message1: "",
    message2: "",
    message3: "",
  });

  const showModal = (
    message1: string,
    message2: string = "",
    message3: string = "",
    title: string = "알림"
  ) => {
    setModalMessage({ title, message1, message2, message3 });
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    history.back();
  };

  // 이제 companyInfo가 확실히 존재하므로 올바른 defaultValues 생성
  const formDefaultValues = useMemo(() => {
    console.log("=== formDefaultValues 생성 ===");
    console.log("companyInfo:", companyInfo);

    const defaultValues = {
      email: companyInfo.email || "",
      companyName: companyInfo.companyName || "",
      businessNumber:
        formatBusinessNumberInput(companyInfo.businessNumber) || "",
      companyAddr: companyInfo.companyAddr || "",
      companyAddrDetail: companyInfo.companyAddrDetail || "",
      mainTelNumber: formatTelInput(companyInfo.mainTelNumber) || "",
      mainBank: companyInfo.mainBank || "",
      mainAccountNumber: companyInfo.mainAccountNumber || "",
      depositor: companyInfo.depositor || "",
      playgroundYn: companyInfo.playgroundYn || "N",
      serviceProvides:
        companyInfo.serviceProvides
          ?.filter((service: any) => service.activeYn === "Y") // eslint-disable-line @typescript-eslint/no-explicit-any
          ?.map((service: any) => service.cdProvideType) // eslint-disable-line @typescript-eslint/no-explicit-any
          ?.join(",") || "",
      facilities:
        companyInfo.facilities
          ?.filter((facility: any) => facility.activeYn === "Y") // eslint-disable-line @typescript-eslint/no-explicit-any
          ?.map((facility: any) => facility.cdFacilityType) // eslint-disable-line @typescript-eslint/no-explicit-any
          ?.join(",") || "",
      weightClasses:
        companyInfo.weightClasses
          ?.filter((weightClass: any) => weightClass.activeYn === "Y") // eslint-disable-line @typescript-eslint/no-explicit-any
          ?.map((weightClass: any) => weightClass.cdGradeType) // eslint-disable-line @typescript-eslint/no-explicit-any
          ?.join(",") || "",
      maxWeight: companyInfo.maxWeight || 0,
      dailyReservCapacity:
        companyInfo.dailyReservCapacity != null ? companyInfo.dailyReservCapacity : "",
      reservCapacityLimitYn: companyInfo.reservCapacityLimitYn || "N",
      agree: true,
      longitude: companyInfo.longitude || "",
      latitude: companyInfo.latitude || "",
    };

    console.log("생성된 defaultValues:", defaultValues);
    return defaultValues;
  }, [companyInfo]);

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    register,
    formState: { errors, isValid },
  } = useForm<BusinessInfoForm>({
    defaultValues: formDefaultValues,
  });

  console.log("=== useForm 생성 완료 ===");
  console.log("formDefaultValues:", formDefaultValues);

  const bindSubmit = async (data: BusinessInfoForm) => {
    if (!companyInfo) return;

    try {
      // 폼 데이터를 API 형식으로 변환
      const updateData = {
        ...companyInfo,
        email: data.email,
        companyName: data.companyName,
        businessNumber: data.businessNumber,
        companyAddr: data.companyAddr,
        companyAddrDetail: data.companyAddrDetail,
        mainTelNumber: data.mainTelNumber,
        mainBank: data.mainBank,
        mainAccountNumber: data.mainAccountNumber,
        depositor: data.depositor,
        playgroundYn: data.playgroundYn,
        serviceProvides: companyInfo.serviceProvides.map((service: any) => ({
          ...service,
          activeYn: data.serviceProvides
            .split(",")
            .includes(service.cdProvideType)
            ? "Y"
            : "N",
        })),
        facilities: companyInfo.facilities.map((facility: any) => ({
          ...facility,
          activeYn: data.facilities.split(",").includes(facility.cdFacilityType)
            ? "Y"
            : "N",
        })),
        weightClasses: companyInfo.weightClasses.map((weightClass: any) => ({
          ...weightClass,
          activeYn: data.weightClasses
            .split(",")
            .includes(weightClass.cdGradeType)
            ? "Y"
            : "N",
        })),
        maxWeight: data.maxWeight,
        dailyReservCapacity:
          data.dailyReservCapacity === "" ? null : Number(data.dailyReservCapacity),
        reservCapacityLimitYn: data.reservCapacityLimitYn,
        longitude: data.longitude || "",
        latitude: data.latitude || "",
      };
      const result = await updateMutation.mutateAsync(updateData);
      if (result.success) {
        showModal(
          "사업자 정보가 성공적으로 수정되었습니다.",
          "",
          "",
          "저장 완료"
        );
      } else {
        showModal(result.message, "", "", "오류");
      }
    } catch (error) {
      console.error("저장 실패:", error);
      showModal("저장 중 오류가 발생했습니다.", "", "", "오류");
    }
  };

  return (
    <>
      <LayoutHeader
        title={"사업자 정보 관리"}
        hasBackBtn
        hasFootPrint={false}
      />
      {/* 수신자 */}
      <div className="flex-auto flex flex-col bg-[#F5F5F5]">
        <form className="flex flex-col gap-4 px-5 pt-5">
          <SequentialAnimation>
            <ValidateInput
              className="w-full"
              placeholder="이메일 주소를 입력하세요"
              label="이메일 주소"
              disabled
              {...register("email")}
            />
            <ValidateInput
              className="w-full"
              label="상호명"
              placeholder="상호명을 입력하세요"
              disabled
              {...register("companyName")}
            />
            <ValidateInput
              className="w-full"
              label="사업자번호"
              disabled
              {...register("businessNumber")}
            />
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                사업장 주소
              </label>
              <Controller
                name="companyAddr"
                control={control}
                rules={{ required: "주소를 검색해주세요." }}
                render={({ field }) => (
                  <BusinessAddressPicker
                    value={field.value}
                    onSelect={(picked) => {
                      // 주소·좌표는 항상 한 번에 갱신되어 불일치가 발생하지 않는다
                      setValue("companyAddr", picked.address, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      setValue("longitude", picked.longitude, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      setValue("latitude", picked.latitude, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                  />
                )}
              />
              <ValidateInput
                className="w-full"
                placeholder="상세주소를 입력하세요"
                {...register("companyAddrDetail")}
              />
            </div>
            <ValidateInput
              className="w-full"
              label="사업장 대표번호"
              type="tel"
              errorMessage={errors?.mainTelNumber?.message}
              {...register("mainTelNumber")}
            />
            <div className="flex flex-col gap-2">
              <Controller
                name="mainBank"
                control={control}
                render={({ field }) => {
                  const bankOptions = bankList;
                  // 현재 값이 있으면 해당 옵션을 첫 번째로 이동
                  const reorderedOptions = field.value
                    ? [
                        bankOptions.find((opt) => opt.value === field.value)!,
                        ...bankOptions.filter(
                          (opt) => opt.value !== field.value
                        ),
                      ]
                    : bankOptions;

                  return (
                    <ValidateSelectList
                      isDefaultValue={!!field.value}
                      onChange={(selectedOption) =>
                        field.onChange(selectedOption?.value || "")
                      }
                      label="정산 계좌 번호"
                      placeholder="은행 선택"
                      isSearchable={true}
                      options={reorderedOptions}
                    />
                  );
                }}
              />
              <ValidateInput
                className="w-full"
                placeholder="예금주"
                errorMessage={errors?.depositor?.message}
                {...register("depositor", {
                  required: { value: true, message: "예금주를 입력하세요" },
                  minLength: {
                    value: 2,
                    message: "이름은 최소 2글자 이상이어야 합니다",
                  },
                  pattern: {
                    value: /^[가-힣]+$/,
                    message: "한글만 입력 가능합니다",
                  },
                })}
              />
              <ValidateInput
                className="w-full"
                placeholder="계좌번호를 입력하세요"
                errorMessage={errors?.mainAccountNumber?.message}
                {...register("mainAccountNumber", {
                  required: { value: true, message: "계좌번호를 입력하세요" },
                })}
              />
            </div>
            <Controller
              name="serviceProvides"
              control={control}
              render={({ field }) => {
                return (
                  <ValidateControllerSelect
                    label="제공 서비스 선택"
                    required={true}
                    value={field.value ? field.value.split(",") : []}
                    onChange={(value) => {
                      field.onChange(value.join(","));
                    }}
                    options={
                      selectList?.providedServices
                        ? transformKeyValueArrayToOptions(
                            selectList.providedServices
                          )
                        : []
                    }
                  />
                );
              }}
            />
            <Controller
              name="playgroundYn"
              control={control}
              render={({ field }) => {
                return (
                  <ValidateControllerSelect
                    label="실외 운동장"
                    required={true}
                    value={field.value ? [field.value] : []}
                    onChange={(value) => {
                      field.onChange(value[0] || "N");
                    }}
                    max={1}
                    options={[
                      { label: "있어요", value: "Y" },
                      { label: "없어요", value: "N" },
                    ]}
                  />
                );
              }}
            />
            <Controller
              name="reservCapacityLimitYn"
              control={control}
              render={({ field }) => {
                return (
                  <ValidateControllerSelect
                    label="예약 정원 제한"
                    required={false}
                    value={field.value ? [field.value] : []}
                    onChange={(value) => {
                      field.onChange(value[0] || "N");
                    }}
                    max={1}
                    options={[
                      { label: "제한함", value: "Y" },
                      { label: "제한 안 함", value: "N" },
                    ]}
                  />
                );
              }}
            />
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">
                일일 예약 정원 (마리)
              </label>
              <input
                type="number"
                min={0}
                placeholder="예: 20"
                disabled={watch("reservCapacityLimitYn") !== "Y"}
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-primary-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400"
                {...register("dailyReservCapacity")}
              />
              <p className="text-xs text-gray-400">
                하루 예약 가능한 최대 강아지 수. ‘제한함’일 때만 적용되며, 정원 도달 날짜는 예약 시 안내됩니다.
              </p>
            </div>
            <Controller
              name="facilities"
              control={control}
              render={({ field }) => {
                return (
                  <ValidateControllerSelect
                    label="시설"
                    required={true}
                    value={field.value ? field.value.split(",") : []}
                    onChange={(value) => {
                      field.onChange(value.join(","));
                    }}
                    options={
                      selectList?.facilities
                        ? transformKeyValueArrayToOptions(selectList.facilities)
                        : []
                    }
                  />
                );
              }}
            />
            <ValidateInput
              className="w-full"
              label="수용 가능 체급"
              placeholder="숫자로 입력하세요. (Kg)"
              errorMessage={errors?.maxWeight?.message}
              {...register("maxWeight", {
                required: {
                  value: true,
                  message: "수용 가능 체급을 입력하세요",
                },
                pattern: {
                  value: /^[0-9]+$/,
                  message: "숫자만 입력 가능합니다",
                },
                min: {
                  value: 1,
                  message: "1 이상의 숫자를 입력하세요",
                },
                max: {
                  value: 100,
                  message: "100 이하의 숫자를 입력하세요",
                },
              })}
            />
            {/* <Controller
              name="weightClasses"
              control={control}
              render={({ field }) => {
                return (
                  <ValidateControllerSelect
                    label="수용 가능 체급"
                    required={true}
                    value={field.value ? field.value.split(",") : []}
                    onChange={(value) => {
                      field.onChange(value.join(","));
                    }}
                    options={
                      selectList?.grades
                        ? transformKeyValueArrayToOptions(selectList.grades)
                        : []
                    }
                  />
                );
              }}
            /> */}
          </SequentialAnimation>
          <Divider className="mt-auto" />
          {/* 
          <SequentialAnimation initialDelay={0.2}>
            <Divider line />
            <Controller
              name="agree"
              control={control}
              rules={{ required: "필수 동의입니다." }}
              render={({ field }) => (
                <ValidateCheckbox
                  defaultChecked={field.value}
                  onChange={() => field.onChange(!field.value)}
                  errorMessage={errors?.agree?.message}
                  title="[필수]사업자 정보 및 제공 서비스에 틀림이 없음을 확인합니다."
                />
              )}
            />
          </SequentialAnimation> */}
        </form>
        <div className="px-5 py-6 w-full">
          <Button
            className="w-full bg-[#F5F5F5]"
            style={{ pointerEvents: "auto" }}
            onClick={handleSubmit(bindSubmit)}
            disabled={!isValid || updateMutation.isPending}
          >
            <div className="py-3 mx-auto w-full text-base font-semibold text-center text-white bg-orange-400 rounded-full">
              {updateMutation.isPending
                ? "저장 중..."
                : "약관 동의하고 수정하기"}
            </div>
          </Button>
        </div>
      </div>

      {/* 저장 결과 메시지 모달 */}
      <Dialog
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">
              {modalMessage.title}
            </div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              {modalMessage.message1}
            </div>
            {modalMessage.message2 && (
              <div className="text-sm text-center text-[#666666]">
                {modalMessage.message2}
              </div>
            )}
            {modalMessage.message3 && (
              <div className="text-sm text-center text-[#666666]">
                {modalMessage.message3}
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleModalClose}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

const bankList = [
  { label: "카카오뱅크", value: "카카오뱅크" },
  { label: "국민은행", value: "국민은행" },
  { label: "기업은행", value: "기업은행" },
  { label: "농협은행", value: "농협은행" },
  { label: "신한은행", value: "신한은행" },
  { label: "iM뱅크", value: "iM뱅크" },
  { label: "산업은행", value: "산업은행" },
  { label: "우리은행", value: "우리은행" },
  { label: "한국씨티은행", value: "한국씨티은행" },
  { label: "하나은행", value: "하나은행" },
  { label: "SC제일은행", value: "SC제일은행" },
  { label: "경남은행", value: "경남은행" },
  { label: "광주은행", value: "광주은행" },
  { label: "도이치은행", value: "도이치은행" },
  { label: "뱅크오브아메리카", value: "뱅크오브아메리카" },
  { label: "부산은행", value: "부산은행" },
  { label: "산림조합중앙회", value: "산림조합중앙회" },
  { label: "저축은행", value: "저축은행" },
  { label: "새마을금고", value: "새마을금고" },
  { label: "수협은행", value: "수협은행" },
  { label: "신협중앙회", value: "신협중앙회" },
  { label: "우체국", value: "우체국" },
  { label: "전북은행", value: "전북은행" },
  { label: "제주은행", value: "제주은행" },
  { label: "중국건설은행", value: "중국건설은행" },
  { label: "중국공상은행", value: "중국공상은행" },
  { label: "중국은행", value: "중국은행" },
  { label: "BNP파리바은행", value: "BNP파리바은행" },
  { label: "HSBC은행", value: "HSBC은행" },
  { label: "JP모간체이스은행", value: "JP모간체이스은행" },
  { label: "케이뱅크", value: "케이뱅크" },
  { label: "토스뱅크", value: "토스뱅크" },
];
